import { Bookmark, ExternalLink, MapPin, Star, MessageSquare, Link2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import api from "../lib/api";
import Countdown from "./Countdown";
import ReviewsModal from "./ReviewsModal";

export default function DealCard({ deal, size = "default" }) {
  const { user, toggleBookmark } = useAuth();
  const bookmarked = user?.bookmarks?.includes(deal.id);
  const expired = deal.expires_at && new Date(deal.expires_at).getTime() < Date.now();
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [summary, setSummary] = useState({
    avg_rating: deal.avg_rating || 0,
    review_count: deal.review_count || 0,
  });

  const handleBookmark = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Please log in to bookmark deals.");
      return;
    }
    try {
      const { action } = await toggleBookmark(deal.id);
      toast.success(action === "added" ? "Bookmarked" : "Removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const isLarge = size === "large";

  const pingClick = () => {
    // Fire-and-forget click tracking so authenticated shoppers become "Verified Buyers" on any review they later leave
    if (!user) return;
    try { api.post(`/deals/${deal.id}/click`).catch(() => {}); } catch (e) { /* ignore */ }
  };

  return (
    <div
      className={`deal-card group relative block border border-[#E5E5E5] bg-white overflow-hidden ${expired ? "opacity-60" : ""}`}
      data-testid={`deal-card-${deal.id}`}
    >
      {/* Stretched primary link — sits above image/text, below interactive buttons (z-10) */}
      <a
        href={deal.url}
        target="_blank"
        rel="noopener noreferrer"
        onMouseDown={pingClick}
        onAuxClick={pingClick}
        aria-label={`Shop ${deal.title}`}
        className="absolute inset-0 z-[1]"
        data-testid={`deal-shop-link-${deal.id}`}
      />
      <div className={`relative overflow-hidden bg-black/5 ${isLarge ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
        {deal.image_url ? (
          <img
            src={deal.image_url}
            alt={deal.title}
            className={`deal-card-img w-full h-full object-cover ${expired ? "grayscale" : ""}`}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-black/20 font-serif text-6xl">◇</div>
        )}
        {deal.discount && (
          <div className="absolute top-3 left-3 bg-[#E63946] text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1" data-testid={`deal-discount-${deal.id}`}>
            {deal.discount}
          </div>
        )}
        {deal.expires_at && (
          <div className="absolute bottom-3 left-3">
            <Countdown expiresAt={deal.expires_at} />
          </div>
        )}
        <button
          onClick={handleBookmark}
          className={`absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center ${bookmarked ? "bg-[#111111] text-white" : "bg-white text-black"} hover:bg-[#E63946] hover:text-white`}
          aria-label="Bookmark deal"
          data-testid={`bookmark-btn-${deal.id}`}
        >
          <Bookmark className="w-4 h-4" strokeWidth={1.5} fill={bookmarked ? "currentColor" : "none"} />
        </button>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setReviewsOpen(true); }}
          className="absolute top-3 right-14 z-10 w-9 h-9 flex items-center justify-center bg-white text-black hover:bg-[#E63946] hover:text-white"
          aria-label="Read and write reviews"
          data-testid={`reviews-open-btn-${deal.id}`}
        >
          <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <Link
          to={`/deals/${deal.id}`}
          onClick={(e) => e.stopPropagation()}
          className="absolute top-3 right-[6.25rem] z-10 w-9 h-9 flex items-center justify-center bg-white text-black hover:bg-[#E63946] hover:text-white"
          aria-label="Open deal details"
          data-testid={`deal-detail-link-${deal.id}`}
          title="Deal details & shareable link"
        >
          <Link2 className="w-4 h-4" strokeWidth={1.5} />
        </Link>
      </div>

      <div className="p-5 relative">
        <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.2em] text-black/60 mb-3 flex-wrap">
          <span data-testid={`deal-category-${deal.id}`}>{deal.category}</span>
          <span className="w-1 h-1 bg-black/30 rounded-full" />
          <Link
            to={`/merchant/${encodeURIComponent(deal.merchant)}`}
            className="relative z-10 hover:text-[#E63946] transition-colors"
            data-testid={`deal-merchant-link-${deal.id}`}
          >
            {deal.merchant}
          </Link>
          {deal.location_city && (
            <>
              <span className="w-1 h-1 bg-black/30 rounded-full" />
              <span className="inline-flex items-center gap-1 text-[#E63946]" data-testid={`deal-city-${deal.id}`}>
                <MapPin className="w-3 h-3" strokeWidth={2} />
                {deal.location_city}
              </span>
            </>
          )}
        </div>
        <h3 className={`font-serif font-bold tracking-tight leading-tight mb-2 ${isLarge ? "text-2xl md:text-3xl" : "text-lg"}`}>
          {deal.title}
        </h3>
        <p className="text-sm text-black/60 leading-relaxed line-clamp-2 mb-4">{deal.description}</p>
        {deal.submitted_by_name && (
          <div className="text-[10px] font-mono uppercase tracking-widest text-black/50 mb-3 flex items-center gap-2 flex-wrap" data-testid={`deal-byline-${deal.id}`}>
            <span>Submitted by <span className="text-[#E63946] font-bold">{deal.submitted_by_name}</span></span>
            {deal.top_contributor && (
              <span className="inline-flex items-center gap-1 bg-[#111111] text-white px-1.5 py-0.5" data-testid={`top-contributor-${deal.id}`} title="Top-10 community contributor">
                <span aria-hidden="true">★</span> Top Contributor
              </span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between border-t border-black/10 pt-4">
          <span className="text-xs font-mono uppercase tracking-widest text-[#E63946]">Shop the deal</span>
          <div className="flex items-center gap-3">
            {summary.review_count > 0 ? (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setReviewsOpen(true); }}
                className="relative z-10 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-black/70 hover:text-[#E63946]"
                data-testid={`deal-rating-badge-${deal.id}`}
              >
                <Star className="w-3 h-3 fill-[#E63946] text-[#E63946]" strokeWidth={1.5} />
                <span>{summary.avg_rating.toFixed(1)}</span>
                <span className="text-black/40">({summary.review_count})</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setReviewsOpen(true); }}
                className="relative z-10 text-[10px] font-bold uppercase tracking-widest text-black/40 hover:text-[#E63946]"
                data-testid={`deal-review-cta-${deal.id}`}
              >
                Be the first to review
              </button>
            )}
            <ExternalLink className="w-4 h-4 group-hover:text-[#E63946]" strokeWidth={1.5} />
          </div>
        </div>
      </div>
      <ReviewsModal
        open={reviewsOpen}
        onOpenChange={setReviewsOpen}
        deal={deal}
        onSummaryChange={setSummary}
      />
    </div>
  );
}
